// HardwareSection.jsx
import React, { useRef } from 'react';
import {
  motion,
  useScroll,
  useSpring,
  useTransform,
  useReducedMotion,
} from 'framer-motion';
import Gear from './Gear';
import PCBLines from './PCBLines';
import EnergyPulse from './EnergyPulse';
import GlowParticles from './GlowParticles';

export default function HardwareSection({
  subtitle = 'Engineered in-house',
  heading = 'Lets Build Products for Smarter life',
  description = 'We design, prototype, and ship mechanical-electronic systems end to end.',
  onCtaClick,
}) {
  const ref = useRef(null);
  const reduce = useReducedMotion();

  // Fixed geometry — one viewBox, scaled via CSS aspect-ratio at every
  // screen size. No JS breakpoints, no extra section height.
  const VB_W = 640;
  const VB_H = 600;
  const gearMain = { cx: 320, cy: 300, r: 135 };
  const gearUp = { cx: 512, cy: 128, r: 60 };
  const gearDown = { cx: 512, cy: 472, r: 60 };

  // Progress goes 0 -> 1 as the section scrolls THROUGH the viewport
  // (top of section entering the bottom of the screen -> bottom of
  // section leaving the top of the screen). No sticky positioning and
  // no extra scroll-room needed — the viewport itself is the "track".
  // Because this is a pure function of scroll position, it automatically
  // reverses when the user scrolls back up.
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start end', 'end start'],
  });

  const p = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    mass: 0.45,
    restDelta: 0.001,
  });

  // Gears: rotate as the section scrolls through view
  const mainRotate = useTransform(p, [0, 0.5, 1], [0, 180, 380]);
  const upRotate = useTransform(p, [0, 0.5, 1], [0, -80, -180]);
  const downRotate = useTransform(p, [0, 0.5, 1], [0, 80, 180]);

  const inFlow = useTransform(p, [0, 0.3, 0.6, 1], [0.3, 1, 1, 0.4]);
  const outFlow = useTransform(p, [0.2, 0.5, 1], [0, 1, 1]);
  const glow = useTransform(p, [0, 0.4, 1], [0.4, 1, 0.7]);
  const satGlow = useTransform(p, [0, 0.4, 1], [0.35, 0.9, 0.6]);
  const haloOpacity = useTransform(p, [0, 0.5, 1], [0.7, 0.5, 0.3]);
  const haloScale = useTransform(p, [0, 0.5, 1], [1, 1.08, 1]);

  // Text: visible while the section is centered in view, fades out as
  // the gear "takes over" mid-scroll, fades back in on scroll-up.
  const textOpacity = useTransform(
  p,
  [0, 0.2, 0.4, 0.6, 0.8, 1],
  [1, 1, 0.9, 0.8, 0, 0]
);
  const textY = useTransform(p, [0, 0.35, 0.65, 1], [24, 0, 0, -24]);

  const pick = (mv, still) => (reduce ? still : mv);

  return (
    <section ref={ref} className="relative overflow-hidden">
      <div
        className="
          mx-auto flex w-full max-w-[1200px] flex-col-reverse items-center
          gap-8 px-4 py-12
          sm:px-6 sm:py-14
          lg:flex-row lg:justify-between lg:gap-10 lg:px-8 lg:py-20
        "
      >
        {/* ---------- Left: content ---------- */}
        <motion.div
          className="w-full min-w-0 text-center text-[rgb(117,61,139)] lg:max-w-[460px] lg:flex-1 lg:text-left"
          style={{ opacity: pick(textOpacity, 1), y: pick(textY, 0) }}
        >
          <div
            className="
              inline-block w-full rounded-2xl bg-white/70 px-5 py-5
              shadow-[0_8px_30px_rgba(124,58,237,0.08)] backdrop-blur-md
              sm:px-6 sm:py-6
              lg:rounded-[20px] lg:px-7 lg:py-6
            "
          >
            <span className="mb-2 block bg-gradient-to-br from-[#ff8fd6] via-[#c86bf0] to-[#8b5cf6] bg-clip-text text-xs font-semibold uppercase tracking-[3px] text-transparent sm:text-sm lg:text-base lg:tracking-[4px]">
              {subtitle}
            </span>

            <h2 className="mb-3 break-words bg-gradient-to-br from-violet-600 via-purple-500 to-pink-500 bg-clip-text font-sans text-2xl font-extrabold leading-[1.2] text-transparent sm:text-3xl lg:mb-4 lg:text-4xl lg:leading-[1.15]">
              {heading}
            </h2>

            <p className="mb-4 break-words font-sans text-sm leading-[1.65] text-[#4a4a6a] sm:text-base lg:mb-5 lg:text-lg lg:leading-[1.75]">
              {description}
            </p>

            {onCtaClick && (
              <button
                type="button"
                onClick={onCtaClick}
                className="inline-block rounded-full bg-gradient-to-br from-pink-500 to-violet-600 px-6 py-2.5 text-sm font-semibold text-white transition-transform duration-200 hover:-translate-y-0.5 hover:shadow-[0_8px_25px_rgba(200,107,240,0.4)]"
              >
                Learn more
              </button>
            )}
          </div>
        </motion.div>

        {/* ---------- Right: hardware animation ---------- */}
        <div className="relative w-full max-w-[420px] lg:max-w-[560px] lg:flex-1">
          <div
            className="relative w-full"
            style={{ aspectRatio: `${VB_W} / ${VB_H}` }}
          >
            <motion.div
              aria-hidden="true"
              className="pointer-events-none absolute left-1/2 top-1/2 z-0 h-[55%] w-[55%] -translate-x-1/2 -translate-y-1/2 rounded-full"
              style={{
                opacity: pick(haloOpacity, 0.6),
                scale: pick(haloScale, 1),
                background:
                  'radial-gradient(circle, rgba(200,107,240,0.15) 0%, rgba(139,92,246,0.08) 40%, rgba(255,143,214,0.05) 70%, transparent 100%)',
              }}
            />

            <svg
              className="absolute inset-0 h-full w-full"
              viewBox={`0 0 ${VB_W} ${VB_H}`}
              role="img"
              aria-label="Animated processor illustration: energy flows through circuit traces into a rotating gear, then out to two smaller gears."
              preserveAspectRatio="xMidYMid meet"
            >
              <defs>
                <linearGradient id="hwBorderGradient" x1="0" y1="0" x2="1" y2="1">
                  <stop offset="0%" stopColor="#ff8fd6" />
                  <stop offset="50%" stopColor="#c86bf0" />
                  <stop offset="100%" stopColor="#8b5cf6" />
                </linearGradient>
              </defs>

              <PCBLines />
              <EnergyPulse inFlow={pick(inFlow, 0.5)} outFlow={pick(outFlow, 0.5)} />

              <Gear
                idPrefix="g-up"
                cx={gearUp.cx}
                cy={gearUp.cy}
                rOuter={gearUp.r}
                teeth={16}
                spokes={5}
                rotate={pick(upRotate, 0)}
                glow={pick(satGlow, 0.45)}
                pulseDelay={0.4}
              />
              <Gear
                idPrefix="g-dn"
                cx={gearDown.cx}
                cy={gearDown.cy}
                rOuter={gearDown.r}
                teeth={16}
                spokes={5}
                rotate={pick(downRotate, 0)}
                glow={pick(satGlow, 0.45)}
                pulseDelay={0.7}
              />
              <Gear
                idPrefix="g-main"
                cx={gearMain.cx}
                cy={gearMain.cy}
                rOuter={gearMain.r}
                teeth={24}
                spokes={6}
                rotate={pick(mainRotate, 0)}
                glow={pick(glow, 0.5)}
                pulseDelay={0}
              />

              <GlowParticles glow={pick(glow, 0.5)} />
            </svg>
          </div>
        </div>
      </div>
    </section>
  );
}